/**
 * The guide format shared by the guide page, the pre-renderer and the tests.
 *
 * A guide is two modules with the same slug:
 *   articles/<slug>.js  what the hub, search engines and the page header need
 *                       (titles, description, dates, FAQs, related guides);
 *                       small, so every page can carry the whole catalog
 *   bodies/<slug>.js    the long part (takeaways, Markdown body, charts,
 *                       methodology, sources), loaded only on the guide's own
 *                       page so the articles never weigh down the app
 *
 * The body is the Help Center's Markdown (src/content/help/markdown.js), plus
 * one extra line form, `::chart <id>`, that places `charts[id]` there.
 */
import { parseMarkdown, toPlainText } from '../help/markdown.js';

export const GUIDES_PATH = '/guides';
export const guidePath = (slug) => `${GUIDES_PATH}/${slug}`;

/** Hub sections, in the order the hub shows them. */
export const GUIDE_CATEGORIES = Object.freeze(['Research', 'Pay over time', 'Booking tips']);

const CHART_LINE = /^::chart\s+([a-z0-9-]+)\s*$/;

/** Body Markdown → [{ type: 'blocks', blocks } | { type: 'chart', id }], in order. */
export function bodySegments(markdown) {
  const segments = [];
  let buf = [];
  const flush = () => {
    const text = buf.join('\n').trim();
    if (text) segments.push({ type: 'blocks', blocks: parseMarkdown(text).blocks });
    buf = [];
  };
  for (const line of String(markdown || '').split('\n')) {
    const m = line.trim().match(CHART_LINE);
    if (m) { flush(); segments.push({ type: 'chart', id: m[1] }); } else buf.push(line);
  }
  flush();
  return segments;
}

/** The body's words as a reader sees them (charts excluded). */
export function bodyText(markdown) {
  return bodySegments(markdown).filter((s) => s.type === 'blocks').map((s) => toPlainText(s.blocks)).join(' ');
}

export const wordCount = (text) => String(text || '').split(/\s+/).filter(Boolean).length;

/** Reading time at ~230 words a minute, never under 3. */
export function readMinutes(body) {
  const words = wordCount(bodyText(body.body)) + wordCount((body.keyTakeaways || []).join(' ')) + wordCount((body.methodology || []).join(' '));
  return Math.max(3, Math.round(words / 230));
}

/** '2026-11-03' → 'November 3, 2026' (UTC, so the server and the browser agree). */
export function formatGuideDate(ymd) {
  const d = new Date(`${ymd}T12:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
